import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";

const validationRegex = /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)(\/[\da-f]+)?\/?$/;

type GenerateEmbedUrlOptions = {
	id: string;
	hash?: string;
};

export function generateEmbedUrl(options: GenerateEmbedUrlOptions): string {
	const {id, hash} = options;
	const params: string = urlEncodeObject(
		{h: hash},
		true
	);

	return `https://player.vimeo.com/video/${encodeURIComponent(id)}?${params}`;
}

export function isVimeoVideoUrl(url: string): boolean {
	return validationRegex.test(url);
}

export type VimeoVideoInfo = {
	id: string;
	hash?: string;
};

export function getInfoFromVimeoUrl(url: string): TryResult<VimeoVideoInfo> {;
	const match = url.match(validationRegex);

	if (match) {
		return {
			wasSuccessful: true,
			result: {
				id: match[1],
				hash: match[2]
			}
		};
	} else {
		return {
			wasSuccessful: false
		};
	}
}

export type VimeoThumbnailUrls = {
	small: string;
	medium: string;
	large: string;
};

type VimeoVideoDetails = {
	thumbnail_small: string;
	thumbnail_medium: string;
	thumbnail_large: string;
};

function isVimeoVideoDetails(data: unknown): data is VimeoVideoDetails {
	if (
		typeof data === "object"
			&& data !== null
			&& "thumbnail_small" in data
			&& "thumbnail_medium" in data
			&& "thumbnail_large" in data
			&& typeof data.thumbnail_small === "string"
			&& typeof data.thumbnail_medium === "string"
			&& typeof data.thumbnail_large === "string"
	) {
		return true;
	}
	return false;
}

export async function getVimeoThumbnailUrls(
	videoUrl: string,
	signal?: AbortSignal
): Promise<VimeoThumbnailUrls> {
	const videoInfo: TryResult<VimeoVideoInfo> = getInfoFromVimeoUrl(videoUrl);

	if (!videoInfo.wasSuccessful) {
		throw new Error(`Could not retrieve video id from: ${videoUrl}`);
	}

	const { id } = videoInfo.result;
	const response: Response = await fetch(`https://vimeo.com/api/v2/video/${id}.json`, { signal });

	if (response.status !== 200) {
		throw new Error(`Failed to fetch video details for video id: ${id}`);
	}

	const data: unknown = await response.json();

	if (!Array.isArray(data) || !isVimeoVideoDetails(data[0])) {
		throw new Error(`Invalid video details received from Vimeo API for video id: ${id}`);
	}

	const videoDetails: VimeoVideoDetails = data[0];

	return {
		small: videoDetails.thumbnail_small,
		medium: videoDetails.thumbnail_medium,
		large: videoDetails.thumbnail_large
	};
}
